import React, { useRef } from 'react';

// Auto-formats raw digits into YYYY-MM-DD as the user types.
function maskDate(input) {
  const digits = String(input).replace(/\D/g, '').slice(0, 8);
  const y = digits.slice(0, 4);
  const m = digits.slice(4, 6);
  const d = digits.slice(6, 8);
  let out = y;
  if (digits.length >= 5) out += `-${m}`;
  else if (digits.length === 4) out += '-';
  if (digits.length >= 7) out += `-${d}`;
  else if (digits.length === 6) out += '-';
  return out;
}

/**
 * Date field that always displays and stores YYYY-MM-DD (locale-independent),
 * unlike a native <input type="date"> whose visible format follows the OS locale.
 * A calendar button opens the native picker for convenience; both keep the value ISO.
 */
export default function DateInput({ label, value, onChange, required, error, disabled }) {
  const nativeRef = useRef();

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  };

  return (
    <label className="block">
      {label && (
        <span className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </span>
      )}
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          placeholder="YYYY-MM-DD"
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange(maskDate(e.target.value))}
          className={`w-full rounded-md border bg-white px-3 py-2 pr-9 text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-gray-100 ${
            error ? 'border-red-400' : 'border-gray-300'
          }`}
        />
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-brand"
          aria-label="Open calendar"
        >
          📅
        </button>
        {/* Hidden native date input drives the calendar picker; value stays ISO. */}
        <input
          ref={nativeRef}
          type="date"
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute right-1 h-0 w-0 opacity-0"
        />
      </div>
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
